import { useEffect, useCallback, useMemo, useState, forwardRef } from "react";
import { ErrorBoundary } from "@datadog/browser-rum-react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getPaginationRowModel,
  getSortedRowModel,
  getGroupedRowModel,
  getExpandedRowModel,
} from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";
import { Virtuoso } from "react-virtuoso";
import { Tooltip } from "react-tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faArrowUp } from "@fortawesome/free-solid-svg-icons";

import Loader from "core/components/Loader";
import Pagination from "./Pagination";
import RowErrorFallback from "./RowErrorFallback";
import {
  Table,
  THead,
  TBody,
  Tr,
  Th,
  Td,
  StickyTHead,
  VirtualTableWrapper,
} from "./styles";

import styled from "styled-components";

import {
  faChevronDown,
  faChevronRight,
} from "@fortawesome/pro-regular-svg-icons";
import { faLayerGroup as faLayerGroupSolid } from "@fortawesome/pro-solid-svg-icons";
import { faLayerGroup as faLayerGroupOutlined } from "@fortawesome/pro-regular-svg-icons";

const EmptyPlaceholder = () => {
  return (
    <div
      style={{
        margin: "15vh",
        textAlign: "center",
      }}
    >
      No records found
    </div>
  );
};

const ScrollerDiv = styled.div`
  &::-webkit-scrollbar {
    width: 12px; // vertical track
    height: 12px; // horizontal track
  }

  &::-webkit-scrollbar-track:vertical {
    margin-top: 33px;
    background: #fafaf9;
    border-left: 1px solid #dddbda;
  }

  &::-webkit-scrollbar-track:horizontal {
    margin-top: 33px;
    background: #fafaf9;
    border-top: 1px solid #dddbda;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 6px;
    border: 2px solid transparent;
    border-right: 1px solid transparent; // This fixes the thumb sitting unevenly in the track for some reason
    border-bottom: 1px solid transparent; // This fixes the thumb sitting unevenly in the track for some reason
    background-clip: padding-box;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.3);
  }

  ::-webkit-scrollbar-corner {
    background-color: #fafaf9;
  }
`;

const VirtualizedScroller = forwardRef(
  ({ context, style, children, ...props }, ref) => (
    <ScrollerDiv data-scroller-div ref={ref} style={style} {...props}>
      {context.stickyHeader}
      {children}
    </ScrollerDiv>
  )
);

VirtualizedScroller.displayName = "VirtualizedScroller";

const HeaderSpacer = ({ context }) => (
  <div data-header-spacer style={{ height: context.headerHeight }} />
);

const TBodyWithState = ({
  isLoading,
  isRefreshing,
  tableRows,
  isVirtualized,
  isGrouped,
  children,
  tableInstance,
  virtualWindowHeightOffset,
}) => {
  const queryClient = useQueryClient();

  if (isLoading || (tableRows.length === 0 && isRefreshing)) {
    return (
      <TBody data-table-body data-table-loading={isLoading || isRefreshing}>
        <div
          style={{
            margin: "15vh",
            textAlign: "center",
          }}
        >
          <Loader />
        </div>
      </TBody>
    );
  }

  if (tableRows.length === 0) {
    return (
      <TBody data-table-body data-table-loading={false}>
        <EmptyPlaceholder />
      </TBody>
    );
  }

  if (isVirtualized) {
    const stickyHeader = (
      <StickyTHead>
        {tableInstance.getHeaderGroups().map((headerGroup) => (
          <Tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              const sorted = header.column.getIsSorted();
              const tooltip = header.column.columnDef.tooltip;
              const hasTooltip = !!tooltip;

              const headerContent = header.isPlaceholder
                ? null
                : flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  );

              return (
                <Th
                  key={header.id}
                  isGrouped={isGrouped}
                  style={{
                    position: "relative",
                    width: header.getSize(),
                    cursor: header.column.getCanSort() ? "pointer" : "text",
                    ...header.column.columnDef.customStyles,
                  }}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  {header.column.getCanGroup() ? (
                    <FontAwesomeIcon
                      icon={
                        header.column.getIsGrouped()
                          ? faLayerGroupSolid
                          : faLayerGroupOutlined
                      }
                      onClick={header.column.getToggleGroupingHandler()}
                      style={{
                        cursor: "pointer",
                        color: "var(--button-contained-background)",
                      }}
                    />
                  ) : null}

                  <span
                    id={header.id}
                    style={{ width: "100%", height: "auto" }}
                  >
                    {headerContent}
                  </span>

                  {hasTooltip && (
                    <Tooltip
                      anchorId={header.id}
                      html={tooltip}
                      positionStrategy="fixed"
                    />
                  )}
                  {sorted ? (
                    <div
                      style={{
                        position: "absolute",
                        right: 0,
                        backgroundColor: "#fafaf9",
                      }}
                    >
                      <FontAwesomeIcon icon={sortIcons[sorted]} />
                    </div>
                  ) : null}
                </Th>
              );
            })}
          </Tr>
        ))}
      </StickyTHead>
    );

    return (
      <VirtualTableWrapper>
        <Virtuoso
          skipAnimationFrameInResizeObserver={true}
          increaseViewportBy={{ bottom: 500, top: 500 }}
          data={tableRows}
          context={{ stickyHeader, headerHeight: 33 }}
          components={{
            Scroller: VirtualizedScroller,
            Header: HeaderSpacer,
            EmptyPlaceholder,
          }}
          itemContent={(index, row) => {
            const baseBgColor = (index + 2) % 2 === 0 ? "#f3f2f2" : "#ffffff";
            return (
              <Tr
                data-test="tableRow"
                style={{
                  backgroundColor: baseBgColor,
                  cursor: row.getCanExpand() ? "pointer" : "normal",
                }}
                hasChanged={row.original.isUpdated}
                onClick={row.getToggleExpandedHandler()}
                onAnimationEnd={() => {
                  if (
                    row.original.isUpdated &&
                    row.original.resetUpdateParams
                  ) {
                    const { queryKey, idKey, rowId } =
                      row.original.resetUpdateParams;
                    queryClient.setQueryData(queryKey, (rows) => {
                      return rows.map((r) => {
                        if (r[idKey] === rowId) {
                          r.isUpdated = false;
                          r.resetUpdateParams = null;
                        }
                        return r;
                      });
                    });
                  }
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <Td
                    key={cell.id}
                    isGrouped={isGrouped}
                    style={{
                      width: cell.column.getSize(),
                      ...cell.column.columnDef.customStyles,
                    }}
                  >
                    {cell.getIsGrouped() ? (
                      <div
                        style={{
                          textOverflow: "ellipsis",
                          overflowX: "clip",
                        }}
                      >
                        <RowExpander
                          isExpanded={row.getIsExpanded()}
                          style={{
                            marginLeft: "2px",
                            marginRight: "8px",
                            cursor: row.getCanExpand() ? "pointer" : "normal",
                            userSelect: "none",
                          }}
                        />
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </div>
                    ) : cell.getIsAggregated() ? (
                      flexRender(
                        cell.column.columnDef.aggregatedCell ??
                          cell.column.columnDef.cell,
                        cell.getContext()
                      )
                    ) : cell.getIsPlaceholder() ? null : (
                      flexRender(cell.column.columnDef.cell, cell.getContext())
                    )}
                  </Td>
                ))}
              </Tr>
            );
          }}
        />
      </VirtualTableWrapper>
    );
  }

  return (
    <TBody data-table-body data-table-loading={isLoading}>
      {children}
    </TBody>
  );
};

const RowExpander = styled(FontAwesomeIcon).attrs({ icon: faChevronRight })`
  transform: rotate(0deg);
  transition: transform 150ms ease-in-out;
  cursor: pointer;

  ${(props) =>
    props.isExpanded &&
    `
    transform: rotate(90deg);
  `}
`;

const AnimatedRow = (props) => {
  const queryClient = useQueryClient();
  const { row, index, selectedRowIds = [], isGrouped } = props;

  const baseBgColor = useMemo(
    () => ((index + 2) % 2 === 0 ? "#f3f2f2" : "#ffffff"),
    [index]
  );
  const selectedBgColor = "#c7d8f0";
  const isSelected = selectedRowIds.includes(row.id);
  const originalBgColor = useMemo(
    () => (isSelected ? selectedBgColor : baseBgColor),
    [isSelected, baseBgColor]
  );

  return (
    <Tr
      data-test="tableRow"
      style={{
        position: "relative",
        backgroundColor: originalBgColor,
        cursor: row.getCanExpand() ? "pointer" : "normal",
      }}
      hasChanged={row.original.isUpdated}
      onClick={row.getToggleExpandedHandler()}
      onAnimationEnd={() => {
        // Remove local update flag
        if (row.original.isUpdated && row.original.resetUpdateParams) {
          const { queryKey, idKey, rowId } = row.original.resetUpdateParams;

          queryClient.setQueryData(queryKey, (rows) => {
            return rows.map((row) => {
              if (row[idKey] === rowId) {
                row.isUpdated = false;
                row.resetUpdateParams = null;
              }

              return row;
            });
          });
        }
      }}
    >
      {row.getVisibleCells().map((cell) => {
        return (
          <Td
            key={cell.id}
            isGrouped={isGrouped}
            style={{
              width: cell.column.getSize(),
              ...cell.column.columnDef.customStyles,
            }}
          >
            {cell.getIsGrouped() ? (
              // If it's a grouped cell, add an expander and row count
              <div
                style={{
                  textOverflow: "ellipsis",
                  overflowX: "clip",
                }}
              >
                <RowExpander
                  isExpanded={row.getIsExpanded()}
                  style={{
                    marginLeft: "2px",
                    marginRight: "8px",
                    cursor: row.getCanExpand() ? "pointer" : "normal",
                    userSelect: "none",
                  }}
                />

                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </div>
            ) : cell.getIsAggregated() ? (
              // If the cell is aggregated, use the Aggregated
              // renderer for cell
              flexRender(
                cell.column.columnDef.aggregatedCell ??
                  cell.column.columnDef.cell,
                cell.getContext()
              )
            ) : cell.getIsPlaceholder() ? null : (
              flexRender(cell.column.columnDef.cell, cell.getContext())
            )}
          </Td>
        );
      })}
    </Tr>
  );
};

const sortIcons = {
  asc: faArrowUp,
  desc: faArrowDown,
};

const ReactTable = (props) => {
  const {
    data,
    columns,
    getRowId,
    defaultState = {},
    isRefreshing,
    searchConfig = { columns: [], searchValue: "" },
    isLoading = false,
    selectedRowIds = [],
    multiSortEnabled = true,
    serverSidePagination = false,
    PaginationComponent,
    serverSideSorting = false,
    handleSortChange,
    sorting,
    columnVisibility,
    pageKey,
    isVirtualized = false,
    isGrouped = false,
    virtualWindowHeightOffset = 0, // used to determine height of virtual table by subtracting from 100vh
  } = props;

  const columnsById = useMemo(
    () =>
      columns?.reduce(
        (obj, col) => ({ ...obj, [col.id || col.accessorKey]: col }),
        {}
      ) || {},
    [columns]
  );

  // const filteredData = data;
  const filteredData = useMemo(() => {
    if (searchConfig?.searchValue && searchConfig?.columns?.length > 0) {
      return data.filter((row) => {
        // Build a string based on the values of all the columns
        const compositeString = searchConfig.columns
          .filter((column) => columnsById[column])
          .map(
            (column) =>
              columnsById[column].accessorKey
                ? row[columnsById[column].accessorKey] // use string based key
                : columnsById[column].accessorFn(row) // or use function to determine key
          )
          .join(" ");

        // Rough check any potential match to keep row in data
        return compositeString
          .toLowerCase()
          .includes(searchConfig.searchValue.toLowerCase());
      });
    }

    return data;
  }, [data, searchConfig, columnsById]);

  const [pagination, setPagination] = useState(
    defaultState?.pagination || {
      pageIndex: 0,
      pageSize: 20,
    }
  );

  useEffect(() => {
    setPagination(
      defaultState?.pagination || {
        pageIndex: 0,
        pageSize: 20,
      }
    );
  }, [defaultState?.pagination, searchConfig?.searchValue]);

  const reactTable = useReactTable({
    data: filteredData,
    columns,
    getRowId,
    initialState: {
      sorting: [{ id: "name", desc: false }],
      ...defaultState,
    },
    state: {
      ...(sorting ? { sorting } : {}),
      columnVisibility,
      pagination,
    },
    onPaginationChange: setPagination,
    meta: {
      searchConfig,
    },
    enableSorting: true,
    enableMultiSort: multiSortEnabled,
    enableSortingRemoval: false,
    enableGrouping: isGrouped,
    autoResetPageIndex: false,
    manualSorting: serverSideSorting,
    manualPagination: serverSidePagination || isVirtualized,
    ...(handleSortChange ? { onSortingChange: handleSortChange } : {}),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  const tableRows = reactTable.getRowModel().rows;

  if (isVirtualized) {
    return (
      <Table
        style={{
          overflowY: "auto",
          minHeight: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <TBodyWithState
          isLoading={isLoading}
          isRefreshing={isRefreshing}
          tableRows={tableRows}
          isVirtualized={isVirtualized}
          isGrouped={isGrouped}
          tableInstance={reactTable}
          virtualWindowHeightOffset={virtualWindowHeightOffset}
        />
      </Table>
    );
  }

  return (
    <div>
      <Table
        style={{
          overflowX: "auto",
        }}
      >
        <THead style={{ userSelect: "none" }}>
          {reactTable.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const sorted = header.column.getIsSorted();
                const tooltip = header.column.columnDef.tooltip;
                const hasTooltip = !!tooltip;

                const headerContent = header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    );

                return (
                  <Th
                    key={header.id}
                    style={{
                      position: "relative",
                      width: header.getSize(),
                      cursor: header.column.getCanSort() ? "pointer" : "text",
                      ...header.column.columnDef.customStyles,
                    }}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <span
                      id={header.id}
                      style={{ width: "100%", height: "auto" }}
                    >
                      {headerContent}
                    </span>

                    {hasTooltip && (
                      <Tooltip
                        anchorId={header.id}
                        html={tooltip}
                        positionStrategy="fixed"
                      />
                    )}
                    {sorted ? (
                      <div
                        style={{
                          position: "absolute",
                          right: 0,
                          backgroundColor: "#fafaf9",
                        }}
                      >
                        {" "}
                        <FontAwesomeIcon icon={sortIcons[sorted]} />
                      </div>
                    ) : null}
                  </Th>
                );
              })}
            </Tr>
          ))}
        </THead>

        <TBodyWithState
          isLoading={isLoading}
          isRefreshing={isRefreshing}
          tableRows={tableRows}
        >
          {tableRows.map((row, rowIndex) => {
            return (
              <ErrorBoundary
                key={row.id}
                onError={console.error}
                fallback={RowErrorFallback}
              >
                <AnimatedRow
                  key={row.id}
                  index={rowIndex}
                  selectedRowIds={selectedRowIds}
                  row={row}
                />
              </ErrorBoundary>
            );
          })}
        </TBodyWithState>
      </Table>

      {PaginationComponent ? (
        PaginationComponent
      ) : (
        <Pagination
          tableInstance={reactTable}
          isRefreshing={isRefreshing}
          pageKey={pageKey}
        />
      )}
    </div>
  );
};

export default ReactTable;
